import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { ContentPlanRootState } from '../../../../elements/contentPlan/store';
import { EFSExperienceId } from '../../../../enums/EFSExperienceId';
import { EFSCompanyDirectionId } from '../../../../enums/EFSCompanyDirectionId';
import { sortEFSItems } from '../EFSGroupItems';
import { ElementAction } from '../../../../elements/elementAction';
import { Functions } from '../../../../types/types';
import { setUnsavedChanges, updateViewState } from '../../viewReducer';
import { setLoading } from '../../../Loader/loaderAction';

type S = ContentPlanRootState;

const loadEFSItemData =
  () =>
  async (dispatch, getState, { getAPI }) => {
    const viewState = getState().view;
    const { view } = getState();
    let efsItemData = view?.efsItemData;
    const cards = Object.values(view.cards) as any;
    const itemIds = cards?.map(card => card.props.itemId).filter(x => x);
    const { getEFSDataDetail } = getAPI();
    if (itemIds.length === 0) {
      return null;
    }
    const efsDataResp = await getEFSDataDetail(itemIds);
    await dispatch(
      updateViewState(viewState?.selected, { efsItemData: efsDataResp || null })
    );
  };

const emptyEFSItemData = () => async (dispatch, getState) => {
  const viewState = getState().view;
  await dispatch(updateViewState(viewState?.selected, { efsItemData: null }));
};

const saveEfsData =
  () =>
  async (dispatch, getState, { getAPI }) => {
    try {
      dispatch(setLoading(true));
      const { view } = getState();
      const { groups, cards: allCards, efsItemData } = view;
      const { updateEFSPersonas } = getAPI();

      const personas = [
        {
          efsCompanyDirectionId: EFSCompanyDirectionId.MaintainDirection,
          efsExperienceId: EFSExperienceId.FirstTime
        },
        {
          efsCompanyDirectionId: EFSCompanyDirectionId.ChangeDirection,
          efsExperienceId: EFSExperienceId.FirstTime
        },
        {
          efsCompanyDirectionId: EFSCompanyDirectionId.MaintainDirection,
          efsExperienceId: EFSExperienceId.Experienced
        },
        {
          efsCompanyDirectionId: EFSCompanyDirectionId.ChangeDirection,
          efsExperienceId: EFSExperienceId.Experienced
        }
      ];
      let efsPersonas = [];
      personas.forEach(persona => {
        groups?.forEach(group => {
          const efsCardRefIds = group?.cardRefIds;
          const filteredCardRefIds = efsCardRefIds.filter(refId => {
            const card = allCards[refId];
            return efsItemData[card.props.itemId]?.efsPersonas.some(
              efs =>
                efs.efsCompanyDirectionId === persona.efsCompanyDirectionId &&
                efs.efsExperienceId === persona.efsExperienceId &&
                efs.efsTimeFrame !== null
            );
          });
          const sorted = sortEFSItems(
            filteredCardRefIds,
            allCards,
            persona,
            efsItemData
          );
          sorted?.forEach((refId, index) => {
            const card = allCards[refId];
            const matchingEfs = efsItemData[
              card.props.itemId
            ]?.efsPersonas.find(
              efs =>
                efs.efsCompanyDirectionId === persona.efsCompanyDirectionId &&
                efs.efsExperienceId === persona.efsExperienceId
            );

            if (matchingEfs) {
              efsPersonas.push({
                itemId: card.props.itemId,
                experienceId: matchingEfs.efsExperienceId,
                companyDirectionId: matchingEfs.efsCompanyDirectionId,
                displayOrder: index
              });
            }
          });
        });
      });

      try {
        const response = await updateEFSPersonas({
          itemEfsPersonas: efsPersonas
        });
        if (!!response) {
          console.log('EFS personas updated successfully');
        } else {
          console.error('Failed to update EFS personas');
        }
        return true;
      } catch (error) {
        console.error('Error updating EFS personas:', error);
        return false;
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

const setEFSPersona =
  (selectedEFSPersona: {
    efsCompanyDirectionId: number;
    efsExperienceId: number;
  }): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const viewState = getState().view;
    dispatch(updateViewState(viewState?.selected, { selectedEFSPersona }));
  };

const onDropEFSItem =
  (
    updatedItems: { refId: string; efsDisplayOrder: number }[]
  ): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const { view } = getState();
    const selectedView = view?.selected;
    const selectedEFSPersona = view?.selectedEFSPersona;

    const updatedCards = { ...view.cards };

    updatedItems.forEach(({ refId, efsDisplayOrder }) => {
      const card = updatedCards[refId];
      if (card) {
        const matchingEfs = card.props.efsData?.efsPersonas.find(
          efs =>
            efs.efsCompanyDirectionId ===
              selectedEFSPersona.efsCompanyDirectionId &&
            efs.efsExperienceId === selectedEFSPersona.efsExperienceId
        );

        if (matchingEfs) {
          matchingEfs.efsDisplayOrder = efsDisplayOrder;
        }
      }
    });

    dispatch(
      updateViewState(selectedView, {
        ...view,
        cards: updatedCards
      })
    );

    dispatch(setUnsavedChangesBoolean(true));
  };

const setUnsavedChangesBoolean =
  (hasUnsavedChanges: boolean): ElementAction<Functions, S> =>
  dispatch => {
    dispatch(setUnsavedChanges(hasUnsavedChanges));
  };

export const useActions = () => {
  const dispatch: Dispatch<any> = useDispatch();
  return {
    emptyEFSItemData: () => dispatch(emptyEFSItemData()),
    loadEFSItemData: () => dispatch(loadEFSItemData()),
    saveEfsData: () => dispatch(saveEfsData()),
    setEFSPersona: (selectedEFSPersona: {
      efsCompanyDirectionId: number;
      efsExperienceId: number;
    }) => dispatch(setEFSPersona(selectedEFSPersona)),
    onDropEFSItem: (
      updatedItems: { refId: string; efsDisplayOrder: number }[]
    ) => dispatch(onDropEFSItem(updatedItems))
  };
};
